import React, { useEffect, useState, useCallback } from "react";
import { Card, Row, Col, Table, Button, Modal, Form, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

const emptyForm = { ir_no: "", ir_gender: "m", ic_id: "", t_ids: [], primary_t_id: "", program_id: "" };

export default function ManageIslamicRoom() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [yearList, setYearList]   = useState([]);
  const [yearId, setYearId]       = useState(localStorage.getItem("yearId") || "");
  const [rooms, setRooms]         = useState([]);
  const [classes, setClasses]     = useState([]);
  const [programs, setPrograms]   = useState([]);
  const [teachers, setTeachers]   = useState([]);
  const [filterClass, setFilterClass] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState(emptyForm);

  useEffect(() => {
    axiosInstance.get("/api/getStudyYear").then(r => {
      setYearList(r.data);
      if (!yearId && r.data.length > 0) {
        const id = String(r.data[0].year_id);
        localStorage.setItem("yearId", id);
        setYearId(id);
      }
    });
    axiosInstance.get("/api/islamic/getIslamicClasses").then(r => setClasses(r.data));
    axiosInstance.get("/api/islamic/getPrograms").then(r => setPrograms(r.data));
    axiosInstance.get("/api/getTeacherList").then(r => setTeachers(r.data));
  }, []);

  const fetchRooms = useCallback(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`/api/islamic/getRooms/${yearId}`)
      .then(r => setRooms(r.data))
      .catch(() => setRooms([]))
      .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const openAdd = () => { setEditTarget(null); setForm({ ...emptyForm, ic_id: classes[0]?.ic_id || "" }); setShowModal(true); };
  const openEdit = (room) => {
    setEditTarget(room);
    const advisors = room.advisors || (room.t_id ? [{ t_id: room.t_id, is_primary: true }] : []);
    const primary = advisors.find(a => a.is_primary) || advisors[0];
    setForm({
      ir_no: room.ir_no,
      ir_gender: room.ir_gender,
      ic_id: room.ic_id,
      t_ids: advisors.map(a => String(a.t_id)),
      primary_t_id: primary ? String(primary.t_id) : "",
      program_id: room.is_programId || "",
    });
    setShowModal(true);
  };

  // เพิ่ม/เอาครูออกจากรายการที่ปรึกษา และดูแลครูหลักให้สอดคล้อง
  const toggleAdvisor = (tId) => {
    const id = String(tId);
    setForm(f => {
      if (f.t_ids.includes(id)) {
        const t_ids = f.t_ids.filter(x => x !== id);
        const primary_t_id = String(f.primary_t_id) === id ? (t_ids[0] || "") : f.primary_t_id;
        return { ...f, t_ids, primary_t_id };
      }
      const t_ids = [...f.t_ids, id];
      return { ...f, t_ids, primary_t_id: f.primary_t_id || id };
    });
  };

  const handleSave = async () => {
    if (!form.ir_no || !form.ir_gender || !form.ic_id) {
      Swal.fire(t.manageRoom.incompleteTitle, t.manageRoom.incompleteMsg, "warning"); return;
    }
    const payload = { ...form, year_id: yearId };
    try {
      if (editTarget) {
        await axiosInstance.post("/api/islamic/updateRoom", { ...payload, ir_id: editTarget.ir_id });
        Swal.fire(t.manageRoom.successTitle, t.manageRoom.successUpdate, "success");
      } else {
        await axiosInstance.post("/api/islamic/addRoom", payload);
        Swal.fire(t.manageRoom.successTitle, t.manageRoom.successAdd, "success");
      }
      setShowModal(false);
      fetchRooms();
    } catch (err) {
      Swal.fire(t.manageRoom.errorTitle, err.response?.data?.message || t.common.error, "error");
    }
  };

  const handleDelete = (room) => {
    Swal.fire({
      title: t.manageRoom.confirmDelete(`${room.ic_class}/${room.ir_no}`),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t.manageRoom.confirmBtn,
      cancelButtonText: t.manageRoom.cancelBtn,
    }).then(r => {
      if (!r.isConfirmed) return;
      axiosInstance.post("/api/islamic/deleteRoom", { ir_id: room.ir_id })
        .then(() => { Swal.fire(t.manageRoom.successTitle, t.manageRoom.successDelete, "success"); fetchRooms(); })
        .catch(err => Swal.fire(t.manageRoom.errorTitle, err.response?.data?.message || t.common.error, "error"));
    });
  };

  const GENDER_LABEL = t.manageRoom.genderLabel;

  return (
    <React.Fragment>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item"><Link to="/dashboard/school-statistic">{t.manageRoom.breadHome}</Link></li>
            <li className="breadcrumb-item active">{t.manageRoom.breadCurrent}</li>
          </ol>
          <h4 className="main-title mb-0">{t.manageRoom.pageTitle}</h4>
        </div>
        <div className="d-flex gap-2 align-items-center mt-2 mt-sm-0 flex-wrap">
          <Form.Select size="sm" value={yearId} onChange={e => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }} style={{ width: 130 }}>
            {yearList.map(y => <option key={y.year_id} value={y.year_id}>{t.common.studyYearShort(y.year_year)}</option>)}
          </Form.Select>
          <Form.Select size="sm" value={filterGender} onChange={e => setFilterGender(e.target.value)} style={{ width: 140 }}>
            <option value="">{t.manageRoom.filterAllGender}</option>
            <option value="m">{t.common.male}</option>
            <option value="f">{t.common.female}</option>
          </Form.Select>
          <Form.Select size="sm" value={filterClass} onChange={e => setFilterClass(e.target.value)} style={{ width: 130 }}>
            <option value="">{t.manageRoom.filterAllClass}</option>
            {classes.map(c => <option key={c.ic_id} value={c.ic_id}>{c.ic_class}</option>)}
          </Form.Select>
          <Button size="sm" variant="primary" onClick={openAdd}><i className="ri-add-line me-1"></i>{t.manageRoom.addBtn}</Button>
        </div>
      </div>

      <Card className="card-one">
        <Card.Header><Card.Title as="h6" className="mb-0">{t.manageRoom.listTitle}</Card.Title></Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead className="table-secondary">
              <tr>
                <th>#</th>
                <th>{t.manageRoom.colClass}</th>
                <th>{t.manageRoom.colRoom}</th>
                <th>{t.manageRoom.colGender}</th>
                <th>{t.manageRoom.colProgram}</th>
                <th>{t.manageRoom.colAdvisor}</th>
                <th>{t.manageRoom.colStudents}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const filtered = rooms
                  .filter(r => !filterClass  || String(r.ic_id)   === String(filterClass))
                  .filter(r => !filterGender || r.ir_gender        === filterGender);
                if (filtered.length === 0) return (
                  <tr><td colSpan={8} className="text-center text-secondary py-4">{t.manageRoom.noRooms}</td></tr>
                );
                return filtered.map((r, i) => (
                <tr key={r.ir_id}>
                  <td className="text-secondary">{i + 1}</td>
                  <td className="fw-medium">{r.ic_class}</td>
                  <td>{r.ir_no}</td>
                  <td><Badge bg={r.ir_gender === "m" ? "info" : "danger"} className="fw-normal">{GENDER_LABEL[r.ir_gender]}</Badge></td>
                  <td>{r.is_programCode || "-"}</td>
                  <td>
                    {r.advisors && r.advisors.length > 0 ? (
                      <div className="d-flex flex-column gap-1">
                        {r.advisors.map(a => (
                          <span key={a.t_id}>
                            {a.is_primary && <i className="ri-star-fill text-warning me-1" title="ครูหลัก"></i>}
                            {a.t_name}
                            {a.t_arabicname && <span className="text-secondary ms-1" dir="rtl">({a.t_arabicname})</span>}
                          </span>
                        ))}
                      </div>
                    ) : r.t_name ? (
                      r.t_name
                    ) : (
                      <span className="text-secondary">-</span>
                    )}
                  </td>
                  <td>
                    <Link to={`/islamic/manage-students/${r.ir_id}`} className="text-decoration-none">
                      <Badge bg="secondary" className="fw-normal">{t.common.personCount(r.student_count)}</Badge>
                    </Link>
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openEdit(r)}><i className="ri-edit-line"></i></Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(r)}><i className="ri-delete-bin-line"></i></Button>
                  </td>
                </tr>
                ));
              })()}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editTarget ? t.manageRoom.modalEditTitle : t.manageRoom.modalAddTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={6}>
              <Form.Label className="fw-medium">{t.manageRoom.labelClass}</Form.Label>
              <Form.Select value={form.ic_id} onChange={e => setForm(f => ({ ...f, ic_id: e.target.value }))}>
                <option value="">{t.manageRoom.classPlaceholder}</option>
                {classes.map(c => <option key={c.ic_id} value={c.ic_id}>{c.ic_class}</option>)}
              </Form.Select>
            </Col>
            <Col xs={6}>
              <Form.Label className="fw-medium">{t.manageRoom.labelRoom}</Form.Label>
              <Form.Control value={form.ir_no} onChange={e => setForm(f => ({ ...f, ir_no: e.target.value }))} placeholder={t.manageRoom.roomPlaceholder} />
            </Col>
            <Col xs={6}>
              <Form.Label className="fw-medium">{t.manageRoom.labelGender}</Form.Label>
              <Form.Select value={form.ir_gender} onChange={e => setForm(f => ({ ...f, ir_gender: e.target.value }))}>
                <option value="m">{t.common.male}</option>
                <option value="f">{t.common.female}</option>
              </Form.Select>
            </Col>
            <Col xs={6}>
              <Form.Label className="fw-medium">{t.manageRoom.labelProgram}</Form.Label>
              <Form.Select value={form.program_id} onChange={e => setForm(f => ({ ...f, program_id: e.target.value }))}>
                <option value="">{t.manageRoom.noneOption}</option>
                {programs.map(p => <option key={p.is_programId} value={p.is_programId}>{p.is_programCode} - {p.is_programName}</option>)}
              </Form.Select>
            </Col>
            <Col xs={12}>
              <Form.Label className="fw-medium">{t.manageRoom.labelAdvisor}</Form.Label>
              <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: "auto" }}>
                {(() => {
                  const advisorTeachers = teachers.filter(tc =>
                    (tc.t_side !== "academic" && tc.t_side !== "officer") ||
                    form.t_ids.includes(String(tc.t_id))
                  );
                  return advisorTeachers.length === 0 ? (
                  <div className="text-muted small">-</div>
                ) : (
                  advisorTeachers.map(tc => {
                    const id = String(tc.t_id);
                    const checked = form.t_ids.includes(id);
                    return (
                      <div key={tc.t_id} className="d-flex align-items-center justify-content-between py-1">
                        <Form.Check
                          type="checkbox"
                          id={`is-adv-${id}`}
                          label={
                            <>
                              {tc.t_name}
                              {tc.t_arabicname && <span className="text-secondary ms-1" dir="rtl">({tc.t_arabicname})</span>}
                            </>
                          }
                          checked={checked}
                          onChange={() => toggleAdvisor(id)}
                        />
                        {checked && (
                          <Form.Check
                            type="radio"
                            name="isPrimaryAdvisor"
                            id={`is-primary-${id}`}
                            label={<i className="ri-star-fill text-warning" title="ครูหลัก"></i>}
                            checked={String(form.primary_t_id) === id}
                            onChange={() => setForm(f => ({ ...f, primary_t_id: id }))}
                          />
                        )}
                      </div>
                    );
                  })
                );
                })()}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>{t.common.cancel}</Button>
          <Button variant="primary" onClick={handleSave}>{t.common.save}</Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
